/**
 * Column visibility configuration for Tasks DataTable
 *
 * This is the single source of truth for:
 * 1. Default visible columns per vertical
 * 2. Column categories for grouping in the visibility menu
 * 3. Column display metadata (names shown in menu)
 *
 * Architecture:
 * - Follows the same pattern as filterConfig.ts
 * - Configuration-driven: add/modify columns in one place
 * - Supports per-vertical customization
 * - Uses shared utilities from common/DataTable
 */

import {
  createColumnGrouper,
  createDisplayNameGetter,
} from '@/components/common/DataTable';

import { TableColumnIds } from './filterConfig';

// ----------------------------------------------------------------------------
// 1. Column Categories (for grouping in menu)
// ----------------------------------------------------------------------------

export const COLUMN_CATEGORIES = {
  TASK_INFO: 'Task Info',
  DATES: 'Dates',
  MATTER: 'Matter',
  PEOPLE: 'People',
  SPECIAL: 'Special',
} as const;

export type ColumnCategory =
  (typeof COLUMN_CATEGORIES)[keyof typeof COLUMN_CATEGORIES];

/**
 * Maps column IDs to their category for grouping in the visibility menu.
 * Columns not in this map are placed in an "Other" category.
 */
export const COLUMN_CATEGORY_MAP: Record<string, ColumnCategory> = {
  // Task Info
  [TableColumnIds.TASK_NAME]: COLUMN_CATEGORIES.TASK_INFO,
  [TableColumnIds.TASK_DESCRIPTION]: COLUMN_CATEGORIES.TASK_INFO,
  [TableColumnIds.TASK_TYPE]: COLUMN_CATEGORIES.TASK_INFO,
  [TableColumnIds.TASK_STATUS]: COLUMN_CATEGORIES.TASK_INFO,
  [TableColumnIds.AGENT]: COLUMN_CATEGORIES.TASK_INFO,
  [TableColumnIds.PRIORITY]: COLUMN_CATEGORIES.TASK_INFO,
  [TableColumnIds.LAST_UPDATE_TEXT]: COLUMN_CATEGORIES.TASK_INFO,

  // Dates
  [TableColumnIds.DUE_DATE]: COLUMN_CATEGORIES.DATES,
  [TableColumnIds.SLA_RESOLUTION_DATE]: COLUMN_CATEGORIES.DATES,
  [TableColumnIds.CREATED_AT]: COLUMN_CATEGORIES.DATES,
  [TableColumnIds.LAST_UPDATE]: COLUMN_CATEGORIES.DATES,

  // Matter
  [TableColumnIds.MATTER_NAME]: COLUMN_CATEGORIES.MATTER,
  [TableColumnIds.CASE_STATUS]: COLUMN_CATEGORIES.MATTER,
  [TableColumnIds.INCIDENT]: COLUMN_CATEGORIES.MATTER,
  [TableColumnIds.FIRM_NAME]: COLUMN_CATEGORIES.MATTER,
  [TableColumnIds.FIRM_PRIMARY_STATE]: COLUMN_CATEGORIES.MATTER,
  [TableColumnIds.COVERAGE]: COLUMN_CATEGORIES.MATTER,

  // People
  [TableColumnIds.ASSIGNEE]: COLUMN_CATEGORIES.PEOPLE,
  [TableColumnIds.LAST_UPDATE_ACTOR]: COLUMN_CATEGORIES.PEOPLE,

  // Special
  [TableColumnIds.RAINMAKER]: COLUMN_CATEGORIES.SPECIAL,
  [TableColumnIds.CMS_STATUS]: COLUMN_CATEGORIES.SPECIAL,
  [TableColumnIds.TASK_SOURCE]: COLUMN_CATEGORIES.SPECIAL,
};

/**
 * Display names for columns in the visibility menu.
 * Falls back to the column header if not specified here.
 */
export const COLUMN_DISPLAY_NAMES: Record<string, string> = {
  [TableColumnIds.RAINMAKER]: 'Rainmaker',
  [TableColumnIds.PRIORITY]: 'Priority',
  [TableColumnIds.DUE_DATE]: 'Next Action',
  [TableColumnIds.SLA_RESOLUTION_DATE]: 'Due Date',
  [TableColumnIds.CREATED_AT]: 'Created',
  [TableColumnIds.MATTER_NAME]: 'Matter',
  [TableColumnIds.CASE_STATUS]: 'Case Status',
  [TableColumnIds.TASK_NAME]: 'Task Name',
  [TableColumnIds.TASK_DESCRIPTION]: 'Description',
  [TableColumnIds.TASK_TYPE]: 'Task Type',
  [TableColumnIds.TASK_STATUS]: 'Status',
  [TableColumnIds.AGENT]: 'Agent',
  [TableColumnIds.ASSIGNEE]: 'Assignee',
  [TableColumnIds.LAST_UPDATE]: 'Last Update At',
  [TableColumnIds.LAST_UPDATE_ACTOR]: 'Last Updated By',
  [TableColumnIds.LAST_UPDATE_TEXT]: 'Last Update',
  [TableColumnIds.INCIDENT]: 'Incident',
  [TableColumnIds.FIRM_NAME]: 'Firm',
  [TableColumnIds.FIRM_PRIMARY_STATE]: 'Firm State',
  [TableColumnIds.COVERAGE]: 'Coverage',
  [TableColumnIds.POLICE_DEPARTMENT]: 'Police Dept',
  [TableColumnIds.FACILITY]: 'Facility',
  [TableColumnIds.CMS_STATUS]: 'CMS Status',
  [TableColumnIds.TASK_SOURCE]: 'Source',
};

// ----------------------------------------------------------------------------
// 2. Default Visible Columns per Vertical
// ----------------------------------------------------------------------------

/**
 * Base default columns shared across most verticals.
 * Order matters - this is the default display order.
 *
 * Hidden by default (can be toggled via column visibility menu):
 * - Task Description, Task Type, Created, Rainmaker, Firm State, Incident, CMS Status, Case Status
 */
export const BASE_DEFAULT_COLUMNS: readonly string[] = [
  TableColumnIds.TASK_NAME,
  TableColumnIds.DUE_DATE,
  TableColumnIds.ASSIGNEE,
  TableColumnIds.TASK_STATUS,
  TableColumnIds.PRIORITY,
  TableColumnIds.MATTER_NAME,
  TableColumnIds.FIRM_NAME,
  TableColumnIds.LAST_UPDATE,
  TableColumnIds.LAST_UPDATE_ACTOR,
  TableColumnIds.LAST_UPDATE_TEXT,
  TableColumnIds.SLA_RESOLUTION_DATE,
] as const;

export function insertAgentColumnNearTaskName(columns: readonly string[]): string[] {
  const nextColumns = columns.filter((columnId) => columnId !== TableColumnIds.AGENT);
  const taskNameIndex = nextColumns.indexOf(TableColumnIds.TASK_NAME);

  if (taskNameIndex === -1) {
    return [TableColumnIds.AGENT, ...nextColumns];
  }

  nextColumns.splice(taskNameIndex + 1, 0, TableColumnIds.AGENT);
  return nextColumns;
}

/**
 * Per-vertical default column overrides.
 * If a vertical is not listed, BASE_DEFAULT_COLUMNS is used.
 *
 * Pattern: Use array spreading to insert vertical-specific columns.
 * Example: [...BASE_DEFAULT_COLUMNS.slice(0, 3), COVERAGE, ...BASE_DEFAULT_COLUMNS.slice(3)]
 */
export const VERTICAL_DEFAULT_COLUMNS: Record<string, readonly string[]> = {
  // Claims: Add Agent next to Task Name and Coverage at end
  claims: [
    ...insertAgentColumnNearTaskName(BASE_DEFAULT_COLUMNS),
    TableColumnIds.COVERAGE,
  ],
  // Liability: Insert Police Department at end
  liability: [...BASE_DEFAULT_COLUMNS, TableColumnIds.POLICE_DEPARTMENT],
  // Medical: Add Agent next to Task Name and Facility at end
  medical: [
    ...insertAgentColumnNearTaskName(BASE_DEFAULT_COLUMNS),
    TableColumnIds.FACILITY,
  ],
};

/**
 * Get default visible columns for a vertical.
 * Returns a new array (safe to mutate).
 */
export function getDefaultColumnsForVertical(verticalId: string): string[] {
  return [...(VERTICAL_DEFAULT_COLUMNS[verticalId] ?? BASE_DEFAULT_COLUMNS)];
}

// ----------------------------------------------------------------------------
// 3. Utility Functions (using shared utilities from common/DataTable)
// ----------------------------------------------------------------------------

/**
 * Get the display name for a column ID.
 * Falls back to the column ID if no display name is configured.
 */
export const getColumnDisplayName =
  createDisplayNameGetter(COLUMN_DISPLAY_NAMES);

/**
 * Get the category for a column ID.
 * Returns undefined if the column is not categorized.
 */
export function getColumnCategory(
  columnId: string,
): ColumnCategory | undefined {
  return COLUMN_CATEGORY_MAP[columnId];
}

/**
 * Group columns by category for display in the visibility menu.
 * Uses shared utility from common/DataTable.
 */
export const groupColumnsByCategory = createColumnGrouper(
  COLUMN_CATEGORY_MAP,
  Object.values(COLUMN_CATEGORIES),
);
